import { apiClient } from "@nabble/sdk";
import { Alert, AuthHeader, AuthLayout, Button } from "@nabble/ui";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/AuthContext";
import { destinationAfterStep, type StepResponse } from "../../../auth/session";
import { errorMessage } from "../../../errors";
import { TERMS_PATH, usePrivacyContact } from "../../../legal/legal";
import { PrivacyNoticeText } from "../../../legal/PrivacyNoticeText";

export default function PrivacyPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { state, applyNext, refreshState, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const version = state?.privacyNoticeVersion ?? null;
  const contact = usePrivacyContact();

  // The version comes from GET /api/auth/state. If that call failed earlier
  // (e.g. rate limited on page load), fetch it again instead of blocking.
  useEffect(() => {
    if (version === null) void refreshState();
  }, [version, refreshState]);

  async function accept(): Promise<void> {
    let current = version;
    if (current === null) current = (await refreshState())?.privacyNoticeVersion ?? null;
    if (current === null) {
      setError("We couldn't load the notice version. Reload the page.");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { next, rememberedUntil } = await apiClient<StepResponse>("/api/auth/privacy", { method: "POST", body: { version: current } });
      applyNext(next, rememberedUntil);
      navigate(destinationAfterStep(next));
    } catch (err) {
      setError(errorMessage(err, "We couldn't record your acceptance. Please try again."));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout wide>
      <AuthHeader
        icon="book"
        title="Privacy notice"
        lead="Before you continue, please read how your personal data is handled."
      />
      <div className="nb-auth-content">
        <div className="nb-doc" tabIndex={0} aria-label="Privacy notice text">
          <PrivacyNoticeText contact={contact} />
          <p className="nb-help" style={{ marginTop: "var(--space-4)" }}>
            {`Notice version: ${version ?? "…"}`}
          </p>
        </div>
        <p className="nb-small">
          By continuing you also accept the{" "}
          <Link to={TERMS_PATH} target="_blank" rel="noopener">
            Terms of use
          </Link>
          .
        </p>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <div className="nb-row" style={{ justifyContent: "flex-end" }}>
          <Button
            label="Sign out"
            variant="ghost"
            onClick={() =>
              void logout().then(() => navigate("/login", { replace: true }))
            }
          />
          <Button label="Accept and continue" size="lg" loading={loading} onClick={() => void accept()} />
        </div>
      </div>
    </AuthLayout>
  );
}
